import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { DemoMode } from './oem-nav';

// =========================================================================
// Demo mode — how much of the build the left rail admits. Fork-owned (zone C).
//
// The port plan offered one lever for the executive cut: flip `TIER_MAX` to 1.
// That lever is the wrong shape. Tier says how finished a surface is, so
// `TIER_MAX = 1` deletes Workflows, Feedback loops, Connectors and Agents —
// the four surfaces that answer "why not Power BI on steroids?" — while
// keeping fourteen rows nobody asked for. Simplification pass A1.
//
// So the cut is a second, orthogonal concept: an explicit allowlist of nav ids
// (`EXECUTIVE_NAV_IDS` in oem-nav.ts). `TIER_MAX` keeps meaning exactly what it
// meant. Nothing is unrouted in executive mode — every surface stays reachable
// by URL, by ⌘K and from in-page links. It is only the rail that gets shorter.
//
// Shape is deliberately BrandContext's, for the same three reasons: a
// module-level default so first paint is deterministic, a try/catch storage
// read applied after mount, and a non-throwing fallback so the snapshot
// renderer (which mounts scenes under the foundation providers only) still
// renders.
// =========================================================================

export type { DemoMode };

/** localStorage key. Namespaced alongside `oem-plus:brand` and `oem-plus:persona`. */
export const DEMO_MODE_STORAGE_KEY = 'oem-plus:demo-mode';

/**
 * Module-level default.
 *
 * Executive, because the room on 1 October is aftermarket executive
 * leadership and the first thing they see is the rail. A presenter who needs
 * the whole build flips one card in Settings → Appearance.
 */
export const DEFAULT_DEMO_MODE: DemoMode = 'executive';

/** Card / menu copy, so the settings cards and the account menu cannot drift. */
export const DEMO_MODE_OPTIONS: readonly {
  id: DemoMode;
  label: string;
  description: string;
}[] = [
  {
    id: 'executive',
    label: 'Executive',
    description:
      'Three sections in the rail: the cost and condition path an aftermarket executive walks. Everything else stays reachable by URL, ⌘K and in-page links.',
  },
  {
    id: 'full',
    label: 'Full',
    description:
      'Every built surface in the rail — workflow modules and steps, feedback loops, connectors and agents. The operating model in full, for a working session.',
  },
];

function isDemoMode(value: unknown): value is DemoMode {
  return value === 'executive' || value === 'full';
}

/** Storage read that survives private mode, blocked site data and SSR. */
function readStoredMode(): DemoMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DEMO_MODE_STORAGE_KEY);
    return isDemoMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredMode(mode: DemoMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEMO_MODE_STORAGE_KEY, mode);
  } catch {
    // Private window / blocked storage — the session still works, it just
    // won't remember the choice.
  }
}

/**
 * The live mode, mirrored outside React.
 *
 * `OemCommandPalette` registers its navigation source once at module scope, so
 * it has no hook to read. It searches every row in both modes either way —
 * full reachability is the whole point of the allowlist — and consults this
 * only to decide which rows to rank first.
 */
let activeMode: DemoMode = DEFAULT_DEMO_MODE;

/** The mode as last applied by the provider. Never throws, never reads storage. */
export function currentDemoMode(): DemoMode {
  return activeMode;
}

type DemoModeContextValue = {
  mode: DemoMode;
  setMode: (mode: DemoMode) => void;
};

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DemoMode>(DEFAULT_DEMO_MODE);

  // Rehydrate after mount so first paint stays deterministic.
  useEffect(() => {
    const stored = readStoredMode();
    if (stored && stored !== mode) setModeState(stored);
    // Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    activeMode = mode;
  }, [mode]);

  const setMode = useCallback((next: DemoMode) => {
    setModeState(next);
    writeStoredMode(next);
    activeMode = next;
  }, []);

  const value = useMemo<DemoModeContextValue>(() => ({ mode, setMode }), [mode, setMode]);

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

/**
 * Value served with no provider mounted — the default mode, read-only.
 *
 * Like `useBrand`, this does not throw: `scripts/parity/render-snapshots.mjs`
 * mounts scenes under the foundation `Providers` only, and a throwing hook
 * would break every snapshot target that reaches a settings pane.
 */
const FALLBACK: DemoModeContextValue = {
  mode: DEFAULT_DEMO_MODE,
  setMode: () => {
    if (import.meta.env?.DEV) {
      console.warn(
        '[oem] setMode() called outside <DemoModeProvider> — the demo mode is ' +
          `fixed to "${DEFAULT_DEMO_MODE}" in this tree.`,
      );
    }
  },
};

export function useDemoMode(): DemoModeContextValue {
  return useContext(DemoModeContext) ?? FALLBACK;
}
